import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CustomerTierBulletToken } from '@sleed/bizcommerce-api';
import { customerTiersApi } from '~/lib/api/client';

/** Admin-managed privilege bullets of one customer tier (BW-8339, from Pharmacy2Go VA-9873). */
export const customerTierBulletKeys = {
	byTier: (tierId: number) => ['customer-tier-bullets', tierId] as const
};

export function useCustomerTierBullets(tierId: number | undefined) {
	return useQuery<CustomerTierBulletToken[]>({
		queryKey: customerTierBulletKeys.byTier(tierId ?? 0),
		queryFn: async () => ((await customerTiersApi.getBullets(tierId)).data.items ?? []).sort((a, b) => (a.si ?? 0) - (b.si ?? 0)),
		enabled: Boolean(tierId)
	});
}

/** Bullets save immediately (own endpoints); the text saves for the request language like the tier's own name. */
export function useCustomerTierBulletMutations(tierId: number) {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: customerTierBulletKeys.byTier(tierId) });
	const create = useMutation({
		mutationFn: async (bullet: CustomerTierBulletToken) => (await customerTiersApi.insertBullet({ ...bullet, tid: tierId })).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (bullet: CustomerTierBulletToken) => {
			// The ML sub-token is never sent: the flat text saves for the request language
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const { btrl, ...rest } = bullet;
			return (await customerTiersApi.updateBullet(rest)).data;
		},
		onSuccess: invalidate
	});
	const remove = useMutation({ mutationFn: async (id: number) => (await customerTiersApi.deleteBullet(id)).data, onSuccess: invalidate });
	return { create, update, remove };
}
